import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CustomButton from "components/common/CustomButton";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomScrollbars from "components/CustomScrollbars";
import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import ManagementPageShell from "components/management/ManagementPageShell";
import {
  MANAGEMENT_TABLE_AREA_SX,
  MANAGEMENT_TABS_SX,
} from "components/management/managementTheme";
import PaginationControls from "components/fileStorage/PaginationControls";
import { FILTER_FIELD_H } from "components/integrations-beta/DateRangeSelect";
import FilesPanel from "components/integrations-beta/FilesPanel";
import IntegrationDetailsHeader from "components/integrations-beta/IntegrationDetailsHeader";
import RunNowDialog from "components/integrations-beta/RunNowDialog";
import RunsFilterBar, {
  type RunsFilters,
} from "components/integrations-beta/RunsFilterBar";
import RunsHistoryTable from "components/integrations-beta/RunsHistoryTable";
import SettingsPanel from "components/integrations-beta/SettingsPanel";
import StepsFilterBar, {
  type StepsFilters,
} from "components/integrations-beta/StepsFilterBar";
import StepExecutionsTable from "components/integrations-beta/StepExecutionsTable";
import StepExecutionDetails from "components/integrations-beta/StepExecutionDetails";
import {
  IntegrationRunListItem,
  IntegrationRunSortField,
  StepExecutionItem,
  StepExecutionSortField,
  useGetIntegrationDetailQuery,
  useGetIntegrationRunDetailQuery,
  useGetIntegrationRunsQuery,
  useTriggerIntegrationRunMutation,
} from "features/integrationsBeta/integrationsBeta";
import {
  useGetIntegrationStepStagesQuery,
  useGetRunStatusesQuery,
  useGetRunTriggerTypesQuery,
  usePlatformEnumLabels,
} from "features/integrationsBeta/platformEnums";
import {
  resolveDisplayZone,
  useTimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import { formatIsoDateTime } from "utils/dateUtils";
import {
  compareDates,
  compareNumbers,
  compareStrings,
  cycleSortField,
  durationMs,
  type SortOrder,
} from "utils/sortUtils";
import Layout from "../components/Layout";

type IntegrationTab = "history" | "files" | "settings";
type DrillLevel = "runs" | "steps" | "step-details";

const DEFAULT_PAGE_SIZE = 10;

function filterStepExecutions(
  steps: StepExecutionItem[],
  filters: StepsFilters,
): StepExecutionItem[] {
  const search = filters.search?.trim().toLowerCase();
  const startedAfter = filters.started_after
    ? Date.parse(filters.started_after)
    : null;
  const startedBefore = filters.started_before
    ? Date.parse(filters.started_before)
    : null;
  const finishedAfter = filters.finished_after
    ? Date.parse(filters.finished_after)
    : null;
  const finishedBefore = filters.finished_before
    ? Date.parse(filters.finished_before)
    : null;

  return steps.filter((s) => {
    if (search && !s.step_name.toLowerCase().includes(search)) return false;
    if (filters.stage && s.stage !== filters.stage) return false;
    if (filters.status && s.status !== filters.status) return false;

    const started = Date.parse(s.started_at);
    if (startedAfter !== null && started < startedAfter) return false;
    if (startedBefore !== null && started > startedBefore) return false;

    const finished = s.finished_at ? Date.parse(s.finished_at) : null;
    if (
      finishedAfter !== null &&
      (finished === null || finished < finishedAfter)
    )
      return false;
    if (
      finishedBefore !== null &&
      (finished === null || finished > finishedBefore)
    )
      return false;

    return true;
  });
}

function sortStepExecutions(
  steps: StepExecutionItem[],
  sortBy: StepExecutionSortField | undefined,
  sortOrder: SortOrder,
): StepExecutionItem[] {
  if (!sortBy) return steps;

  const sorted = [...steps];
  sorted.sort((a, b) => {
    switch (sortBy) {
      case "stage":
        return compareStrings(a.stage, b.stage, sortOrder);
      case "stage_order":
        return compareNumbers(a.stage_order, b.stage_order, sortOrder);
      case "step_name":
        return compareStrings(a.step_name, b.step_name, sortOrder);
      case "started_at":
        return compareDates(a.started_at, b.started_at, sortOrder);
      case "finished_at":
        return compareDates(a.finished_at, b.finished_at, sortOrder);
      case "duration":
        return compareNumbers(
          durationMs(a.started_at, a.finished_at),
          durationMs(b.started_at, b.finished_at),
          sortOrder,
        );
      case "status":
        return compareStrings(a.status, b.status, sortOrder);
      default:
        return 0;
    }
  });
  return sorted;
}

export default function IntegrationBetaDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const { statusLabel, stageLabel, triggerLabel } = usePlatformEnumLabels();
  const { canEdit, canView } = usePlatformRole();
  const { data: runStatuses } = useGetRunStatusesQuery();
  const { data: runTriggerTypes } = useGetRunTriggerTypesQuery();
  const { data: stepStages } = useGetIntegrationStepStagesQuery();

  const integrationId = id ? parseInt(id, 10) : NaN;
  const integrationIdValid = !Number.isNaN(integrationId);

  // Deep link: the wizard (and User Management) navigate here with
  // location.state to open a specific tab (e.g. Settings, where the
  // readiness banner guides the remaining setup). Read once, lazily.
  const locationState = location.state as {
    initialTab?: IntegrationTab;
    fromUserManagement?: boolean;
  } | null;
  const [integrationTab, setIntegrationTab] = useState<IntegrationTab>(() =>
    locationState?.initialTab === "settings" ||
    locationState?.initialTab === "files"
      ? locationState.initialTab
      : "history",
  );
  const [drillLevel, setDrillLevel] = useState<DrillLevel>("runs");
  const [selectedRun, setSelectedRun] = useState<IntegrationRunListItem | null>(
    null,
  );
  const [selectedStepExecutionId, setSelectedStepExecutionId] = useState<
    number | null
  >(null);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [runsSortBy, setRunsSortBy] = useState<
    IntegrationRunSortField | undefined
  >();
  const [runsSortOrder, setRunsSortOrder] = useState<SortOrder>("asc");
  const [runsFilters, setRunsFilters] = useState<RunsFilters>({});
  const [stepsSortBy, setStepsSortBy] = useState<
    StepExecutionSortField | undefined
  >();
  const [stepsSortOrder, setStepsSortOrder] = useState<SortOrder>("asc");
  const [stepsFilters, setStepsFilters] = useState<StepsFilters>({});

  const [triggerRun, { isLoading: isTriggering }] =
    useTriggerIntegrationRunMutation();
  const [runNowOpen, setRunNowOpen] = useState(false);
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  const [timezoneMode, setTimezoneMode] = useTimezoneDisplayMode();
  // Pending Settings edits guard tab switches and the Back button.
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<
    null | { kind: "tab"; tab: IntegrationTab } | { kind: "back" }
  >(null);
  // Set the moment the integration is deleted, so this page's queries go quiet
  // while the cache invalidation settles and React unmounts it - otherwise
  // they refetch a row that no longer exists.
  const [deleted, setDeleted] = useState(false);

  const {
    data: integration,
    isFetching: isFetchingIntegration,
    isError: integrationLoadFailed,
  } = useGetIntegrationDetailQuery(integrationId, {
    skip: !integrationIdValid || deleted,
  });

  // The Files tab browses the managed integration archive - only pipelines
  // with platform.storage.* steps write there, so others hide the tab.
  const hasIntegrationStorage = (integration?.steps ?? []).some((step) =>
    step.step_key?.startsWith("platform.storage."),
  );
  useEffect(() => {
    if (integration && integrationTab === "files" && !hasIntegrationStorage) {
      setIntegrationTab("history");
    }
  }, [integration, integrationTab, hasIntegrationStorage]);

  // Zone the History timestamps render in; Client mode = the integration's
  // own timezone (one zone for the whole page).
  const displayZone = resolveDisplayZone(timezoneMode, integration?.timezone);

  const goBack = () =>
    locationState?.fromUserManagement
      ? navigate(-1)
      : navigate("/integrations-beta");

  const runsQueryArgs = useMemo(
    () => ({
      integration_id: integrationId,
      page,
      page_size: pageSize,
      ...runsFilters,
      ...(runsSortBy ? { sort_by: runsSortBy, sort_order: runsSortOrder } : {}),
    }),
    [integrationId, page, pageSize, runsFilters, runsSortBy, runsSortOrder],
  );

  const {
    data: runsData,
    isLoading: isLoadingRuns,
    isFetching: isFetchingRuns,
  } = useGetIntegrationRunsQuery(runsQueryArgs, {
    skip: !integrationIdValid || deleted,
  });

  const runs = runsData?.results ?? [];
  const totalRuns = runsData?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRuns / pageSize));

  const { data: runDetail, isFetching: isFetchingRunDetail } =
    useGetIntegrationRunDetailQuery(selectedRun?.id ?? 0, {
      skip: !selectedRun,
    });

  const steps: StepExecutionItem[] = useMemo(() => {
    if (!runDetail) return [];
    const filtered = filterStepExecutions(
      runDetail.step_executions,
      stepsFilters,
    );
    return sortStepExecutions(filtered, stepsSortBy, stepsSortOrder);
  }, [runDetail, stepsFilters, stepsSortBy, stepsSortOrder]);

  const selectedStep =
    selectedStepExecutionId != null
      ? (steps.find((s) => s.id === selectedStepExecutionId) ?? null)
      : null;

  const goToRuns = () => {
    setDrillLevel("runs");
    setSelectedRun(null);
    setSelectedStepExecutionId(null);
    setStepsSortBy(undefined);
    setStepsSortOrder("asc");
    setStepsFilters({});
  };

  const goToSteps = () => {
    setDrillLevel("steps");
    setSelectedStepExecutionId(null);
  };

  const handleDrillBack = () => {
    if (drillLevel === "step-details") {
      goToSteps();
      return;
    }
    if (drillLevel === "steps") {
      goToRuns();
    }
  };

  const handleSelectRun = (run: IntegrationRunListItem) => {
    setSelectedRun(run);
    setSelectedStepExecutionId(null);
    setStepsSortBy(undefined);
    setStepsSortOrder("asc");
    setStepsFilters({});
    setDrillLevel("steps");
  };

  const handleSelectStep = (step: StepExecutionItem) => {
    setSelectedStepExecutionId(step.id);
    setDrillLevel("step-details");
  };

  const handleRunsSortChange = (field: IntegrationRunSortField) => {
    const next = cycleSortField(runsSortBy, field, runsSortOrder);
    setRunsSortBy(next.sortBy);
    setRunsSortOrder(next.sortOrder);
    setPage(1);
  };

  const handleStepsSortChange = (field: StepExecutionSortField) => {
    const next = cycleSortField(stepsSortBy, field, stepsSortOrder);
    setStepsSortBy(next.sortBy);
    setStepsSortOrder(next.sortOrder);
  };

  const handleRunsFiltersChange = (next: RunsFilters) => {
    setRunsFilters(next);
    setPage(1);
  };

  const triggerWithPayload = async (
    event_payload?: Record<string, unknown>,
  ) => {
    try {
      await triggerRun({ integrationId, event_payload }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Run triggered. It will appear in the history shortly.",
        }),
      );
      setPage(1);
      setRunNowOpen(false);
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not trigger the run.",
        }),
      );
    }
  };

  const handleRunNow = () => {
    // A webhook-driven integration's source step consumes the event payload -
    // ask for it instead of firing an empty (guaranteed-to-fail) run.
    if (integration?.trigger_mode === "WEBHOOK") {
      setRunNowOpen(true);
      return;
    }
    setRunConfirmOpen(true);
  };

  // Deleting an integration makes its URL permanently 404, which a stale
  // bookmark or a second tab can still hit.
  const handleIntegrationDeleted = () => {
    setSettingsDirty(false);
    setPendingLeave(null);
    setDeleted(true);
    // replace: the dead detail URL must not stay reachable via Back/Forward.
    navigate("/integrations-beta", { replace: true });
  };

  if (!integrationIdValid) {
    return (
      <Layout
        title="Integration Details"
        content="Integration Details"
        noScroll
      >
        <ManagementPageShell title="Integration Details">
          <Typography color="error">Invalid integration id.</Typography>
        </ManagementPageShell>
      </Layout>
    );
  }

  if (!deleted && integrationLoadFailed && !integration) {
    return (
      <Layout
        title="Integration Details"
        content="Integration Details"
        noScroll
      >
        <ManagementPageShell title="Integration Details">
          <Typography color="error" gutterBottom>
            This integration no longer exists.
          </Typography>
          <CustomButton
            fullWidth={false}
            onClick={() => navigate("/integrations-beta")}
          >
            Back to integrations
          </CustomButton>
        </ManagementPageShell>
      </Layout>
    );
  }

  return (
    <Layout title="Integration Details" content="Integration Details" noScroll>
      <ManagementPageShell
        title="Integration Details"
        headerExtra={
          <CustomButton
            fullWidth={false}
            onClick={() => {
              if (integrationTab === "settings" && settingsDirty) {
                setPendingLeave({ kind: "back" });
                return;
              }
              goBack();
            }}
          >
            Back
          </CustomButton>
        }
        beforePanel={
          integration ? (
            <IntegrationDetailsHeader
              integration={integration}
              statusLabel={statusLabel}
              timezoneMode={timezoneMode}
              onTimezoneModeChange={setTimezoneMode}
            />
          ) : undefined
        }
      >
        {isFetchingIntegration && !integration ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={integrationTab}
              onChange={(_, value: IntegrationTab) => {
                if (
                  integrationTab === "settings" &&
                  value !== "settings" &&
                  settingsDirty
                ) {
                  setPendingLeave({ kind: "tab", tab: value });
                  return;
                }
                setIntegrationTab(value);
              }}
              variant={isMobileLayout ? "scrollable" : "standard"}
              scrollButtons={isMobileLayout ? "auto" : false}
              allowScrollButtonsMobile
              sx={{
                flexShrink: 0,
                borderBottom: 1,
                borderColor: "divider",
                mb: 1.5,
                ...MANAGEMENT_TABS_SX,
              }}
            >
              <Tab label="History" value="history" />
              {hasIntegrationStorage && <Tab label="Files" value="files" />}
              <Tab label="Settings" value="settings" />
            </Tabs>

            {integrationTab === "history" && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {drillLevel !== "runs" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                      minHeight: 36,
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip title="Back">
                      <IconButton
                        size="small"
                        onClick={handleDrillBack}
                        aria-label="Go back"
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Breadcrumbs
                      separator={<NavigateNextIcon fontSize="small" />}
                      aria-label="run drill-down breadcrumb"
                      sx={{ minWidth: 0 }}
                    >
                      <MuiLink
                        component="button"
                        underline="hover"
                        color="primary"
                        onClick={goToRuns}
                        sx={{ fontWeight: 600 }}
                      >
                        Runs history
                      </MuiLink>

                      {selectedRun &&
                        (drillLevel === "steps" ? (
                          <Typography
                            color="text.primary"
                            fontWeight={600}
                            noWrap
                          >
                            Run ·{" "}
                            {formatIsoDateTime(
                              selectedRun.started_at,
                              displayZone,
                            )}
                          </Typography>
                        ) : (
                          <MuiLink
                            component="button"
                            underline="hover"
                            color="primary"
                            onClick={goToSteps}
                            sx={{ fontWeight: 600 }}
                            noWrap
                          >
                            Run ·{" "}
                            {formatIsoDateTime(
                              selectedRun.started_at,
                              displayZone,
                            )}
                          </MuiLink>
                        ))}

                      {drillLevel === "step-details" && selectedStep && (
                        <Typography
                          color="text.primary"
                          fontWeight={600}
                          noWrap
                        >
                          Step · {selectedStep.step_name}
                        </Typography>
                      )}
                    </Breadcrumbs>

                    {selectedRun?.logs_url && (
                      <>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title="Open this run's log lines in CloudWatch">
                          <Button
                            component="a"
                            href={selectedRun.logs_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{
                              flexShrink: 0,
                              height: FILTER_FIELD_H,
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                            }}
                          >
                            View logs
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                )}

                {drillLevel === "runs" && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      mb: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <RunsFilterBar
                      filters={runsFilters}
                      onChange={handleRunsFiltersChange}
                      statuses={runStatuses ?? []}
                      triggers={runTriggerTypes ?? []}
                      showTrigger={canView}
                    />
                    {canEdit && (
                      <CustomButton
                        fullWidth={false}
                        onClick={handleRunNow}
                        disabled={isTriggering}
                        startIcon={<PlayArrowIcon />}
                      >
                        Run now
                      </CustomButton>
                    )}
                    {runNowOpen && (
                      <RunNowDialog
                        open
                        onClose={() => setRunNowOpen(false)}
                        onSubmit={(payload) => triggerWithPayload(payload)}
                        loading={isTriggering}
                      />
                    )}
                    <ConfirmDialog
                      open={runConfirmOpen}
                      title="Run integration now"
                      message={`Trigger a run of "${integration?.name ?? ""}" now? It will execute the full pipeline immediately.`}
                      confirmLabel="Run now"
                      loading={isTriggering}
                      onConfirm={async () => {
                        await triggerWithPayload();
                        setRunConfirmOpen(false);
                      }}
                      onCancel={() => setRunConfirmOpen(false)}
                    />
                  </Box>
                )}

                {drillLevel === "steps" && (
                  <StepsFilterBar
                    filters={stepsFilters}
                    onChange={setStepsFilters}
                    stages={stepStages ?? []}
                    statuses={runStatuses ?? []}
                  />
                )}

                <Box
                  sx={{ ...MANAGEMENT_TABLE_AREA_SX, flexDirection: "column" }}
                >
                  {drillLevel === "runs" && (
                    <RunsHistoryTable
                      runs={runs}
                      isLoading={isLoadingRuns}
                      isFetching={isFetchingRuns}
                      selectedRunId={selectedRun?.id ?? null}
                      onSelectRun={handleSelectRun}
                      sortBy={runsSortBy}
                      sortOrder={runsSortOrder}
                      onSortChange={handleRunsSortChange}
                      statusLabel={statusLabel}
                      triggerLabel={triggerLabel}
                      timezoneMode={timezoneMode}
                      displayZone={displayZone}
                      pagination={
                        totalRuns > 0 ? (
                          <Box
                            sx={{
                              position: "relative",
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <PaginationControls
                              currentLimit={pageSize}
                              onLimitChange={(limit) => {
                                setPageSize(limit);
                                setPage(1);
                              }}
                              totalItems={totalRuns}
                              currentPage={page}
                              totalPages={totalPages}
                              onPageChange={setPage}
                              showLimitSelector={true}
                              showPageInfo={true}
                              disabled={isFetchingRuns}
                              size="small"
                              pageTokens={[]}
                              hasNextPage={page < totalPages}
                              borderStyle={{
                                borderTop: "none",
                                borderColor: "none",
                              }}
                              buttonStyle={{
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          </Box>
                        ) : null
                      }
                    />
                  )}

                  {drillLevel === "steps" &&
                    (isFetchingRunDetail && !runDetail ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ p: 6, flex: 1 }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <StepExecutionsTable
                        steps={steps}
                        selectedStepExecutionId={selectedStepExecutionId}
                        onSelectStep={handleSelectStep}
                        sortBy={stepsSortBy}
                        sortOrder={stepsSortOrder}
                        onSortChange={handleStepsSortChange}
                        statusLabel={statusLabel}
                        stageLabel={stageLabel}
                        timezoneMode={timezoneMode}
                        displayZone={displayZone}
                      />
                    ))}

                  {drillLevel === "step-details" && selectedStep && (
                    <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
                      <Box sx={{ p: 2, pr: 2.5 }}>
                        <StepExecutionDetails
                          step={selectedStep}
                          statusLabel={statusLabel(selectedStep.status)}
                          stageLabel={stageLabel(selectedStep.stage)}
                          timezoneMode={timezoneMode}
                          displayZone={displayZone}
                        />
                      </Box>
                    </CustomScrollbars>
                  )}
                </Box>
              </Box>
            )}

            {integrationTab === "files" &&
              hasIntegrationStorage &&
              integration && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <FilesPanel
                    integrationId={integration.id}
                    integrationName={integration.name}
                  />
                </Box>
              )}

            {integrationTab === "settings" && integration && (
              // trackOffsetTop clears the sticky sub-tab bar; the content's
              // right padding keeps the track off the section cards.
              <CustomScrollbars
                sx={{ flex: 1, minHeight: 0 }}
                trackOffsetTop={56}
              >
                <Box sx={{ pb: 1, pr: 1.5 }}>
                  <SettingsPanel
                    integration={integration}
                    onDirtyChange={setSettingsDirty}
                    onDeleted={handleIntegrationDeleted}
                  />
                </Box>
              </CustomScrollbars>
            )}
          </>
        )}

        <UnsavedPlatformDraftsDialog
          open={pendingLeave != null}
          onClose={() => setPendingLeave(null)}
          rows={[]}
          title="Unsaved integration settings"
          intro="You have unsaved changes in Settings. Stay to review and save them, or discard them and continue."
          onDiscardAndContinue={() => {
            const leave = pendingLeave;
            setPendingLeave(null);
            setSettingsDirty(false);
            if (!leave) return;
            // Leaving unmounts SettingsPanel; the pending store dies with it.
            if (leave.kind === "tab") setIntegrationTab(leave.tab);
            else goBack();
          }}
        />
      </ManagementPageShell>
    </Layout>
  );
}
